import { STORAGE_KEYS } from "../../../js/constants";
import MotiveLightbox from "./motive-lightbox/motive-lightbox";

class Motive {
  /**
   * @param {HTMLElement} element - the motive element
   */
  constructor(element) {
    this.element = element;

    this.config = {
      selectors: {
        lightBoxPreviewPDFText: ".js-MotiveLightbox-text",
        lightBoxPreviewPDFImage: ".js-MotiveLightbox-image",
        previewImage: ".js-Motive-previewImage",
        previewButton: ".js-Motive-previewButton",
        radio: ".js-Motive-radio",
        variantImage: ".js-Motive-variantImage",
        imageConfirmationCheckbox: ".js-Motive-imageConfirmationCheckbox",
        greetingsTextarea: "textarea",
        lightbox: ".js-MotiveLightbox",
      },
    };

    this.radios = [...element.querySelectorAll(this.config.selectors.radio)];

    this.previewImage = element.querySelector(
      this.config.selectors.previewImage
    );

    this.imageConfirmationCheckbox = element.querySelector(
      this.config.selectors.imageConfirmationCheckbox
    );

    this.lightBoxPreviewPDFText = element.querySelector(
      this.config.selectors.lightBoxPreviewPDFText
    );

    this.lightBoxPreviewPDFImage = element.querySelector(
      this.config.selectors.lightBoxPreviewPDFImage
    );

    this.previewButton = element.querySelector(
      this.config.selectors.previewButton
    );

    this.greetingsTextarea = element.querySelector(
      this.config.selectors.greetingsTextarea
    );

    this.lightboxElement = element.querySelector(
      this.config.selectors.lightbox
    );

    this.motiveLightbox = new MotiveLightbox(this.lightboxElement);

    this._updatePreviewImageAttributes =
      this._updatePreviewImageAttributes.bind(this);

    this._enablePreviewButton = this._enablePreviewButton.bind(this);

    this._init();
  }

  /**
   * init
   *
   * @private
   */
  _init() {
    this.radios.forEach((radio) => {
      radio.addEventListener("change", this._updatePreviewImageAttributes);
    });

    this.imageConfirmationCheckbox.addEventListener(
      "change",
      this._enablePreviewButton
    );

    this.greetingsTextarea.addEventListener("input", this._enablePreviewButton);

    this._enablePreviewButton(this.imageConfirmationCheckbox);

    this.previewButton.addEventListener("click", () => {
      this.motiveLightbox.updatePreviewPDF(
        this.greetingsTextarea.value,
        `motive-variant-${
          this.element.querySelector(`${this.config.selectors.radio}:checked`)
            .value
        }`
      );
    });

    this._checkSessionStorage();
  }

  /**
   * Check session storage if user has previously chosen a motive variant and update the preview image
   * It enables the motive image to be set to its chosen state if the form encounters an error on submit
   */
  _checkSessionStorage() {
    const motiveVariant = sessionStorage.getItem(STORAGE_KEYS.MOTIVE_VARIANT);

    if (!motiveVariant) return;

    const imageSource = JSON.parse(motiveVariant).src;

    const variantImage = this.element.querySelector(
      `${this.config.selectors.variantImage}[src="${imageSource}"]`
    );

    if (!variantImage) return;

    const radioId = variantImage.parentElement?.getAttribute("for");

    const imageRadio = this.radios.find((radio) => radio.id === radioId);
    if (imageRadio) {
      this.radios[0].removeAttribute("checked"); // First radio is selected by default
      imageRadio.setAttribute("checked", "checked");

      this.previewImage.setAttribute("src", variantImage.getAttribute("src"));
      this.previewImage.setAttribute("alt", variantImage.getAttribute("alt"));
    }
  }

  /**
   * update preview image src and alt with the src and the alt from the chosen variant
   *
   * @param {object} event
   * @private
   */
  _updatePreviewImageAttributes(event) {
    const checkedImage = event.target.nextElementSibling.querySelector(
      this.config.selectors.variantImage
    );
    this.previewImage.setAttribute("src", checkedImage.getAttribute("src"));
    this.previewImage.setAttribute("alt", checkedImage.getAttribute("alt"));

    sessionStorage.setItem(
      STORAGE_KEYS.MOTIVE_VARIANT,
      JSON.stringify({
        src: checkedImage.getAttribute("src"),
        alt: checkedImage.getAttribute("alt"),
      })
    );

    if (this.imageConfirmationCheckbox.checked) {
      this.imageConfirmationCheckbox.checked = false;

      this._enablePreviewButton();
    }
  }

  /**
   * Enable the lightbox opener button if the user has confirmed the motive image and added the greetings text
   */
  _enablePreviewButton() {
    if (
      this.imageConfirmationCheckbox.checked &&
      this.greetingsTextarea.value
    ) {
      this.previewButton.disabled = false;
    } else {
      this.previewButton.disabled = true;
    }
  }
}

export default Motive;
